import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Card, Row, Col, Spinner } from "react-bootstrap";
import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import TextInput from "../components/TextInput";
import FileInput from "../components/FileInput";
import LocationSelector from "../components/LocationSelector";
import AlertService from "../../services/AlertService";
import Swal from "sweetalert2";
import LocationPickerMap, { LocationPickerRef } from "../components/LocationPickerMap";

interface BusinessInfo {
  id?: number;
  name: string;
  logo: string | File | null;
  mobile_no: string;
  email: string;
  division_id: string;
  district_id: string;
  thana_id: string;
  postal_code: string;
  address: string;
  lat: number;
  lon: number;
  map_image: string | File | null;
}

export default function EditBusinessInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const existingInfo = location.state?.info || null;
  const isEditMode = !!existingInfo;

  const [formData, setFormData] = useState<BusinessInfo>({
    id: existingInfo?.id,
    name: existingInfo?.name || "",
    logo: existingInfo?.logo || null,
    mobile_no: existingInfo?.mobile_no || "",
    email: existingInfo?.email || "",
    division_id: existingInfo?.division_id || "",
    district_id: existingInfo?.district_id || "",
    thana_id: existingInfo?.thana_id || "",
    postal_code: existingInfo?.postal_code || "",
    address: existingInfo?.address || "",
    lat: existingInfo?.latitude || 23.8103,
    lon: existingInfo?.longitude || 90.4125,
    map_image: existingInfo?.map_image || null,
  });

  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string | null>(
    typeof existingInfo?.logo_path === "string" ? existingInfo.logo_path : null
  );

  // ---- Common Field Change ----
  const handleFieldChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const mapRef = useRef<LocationPickerRef>(null);

  // const submitCreate = async () => {
  //   const mapImage = await mapRef.current?.generateStaticImage();

  //   const fd = new FormData();
  //   if (formData.lat && formData.lon) {
  //     fd.append("lat", formData.lat.toString());
  //     fd.append("lng", formData.lon.toString());
  //   }

  //   if (mapImage) {
  //     fd.append("map_image", mapImage);
  //   }

  //   await fetch("/api/business/store", {
  //     method: "POST",
  //     body: fd,
  //   });
  // };

  // ---- File (Logo) Change ----
  const handleLogoChange = (name: any, files: any) => {
    if (files && files.length > 0) {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }));
      const previewURL = URL.createObjectURL(file);
      setPreview(previewURL);
    }
  };

  // ---- Validation ----
  const validateForm = () => {
    const requiredFields = [
      "name",
      "mobile_no",
      "email",
      "division_id",
      "district_id",
      "thana_id",
      "postal_code",
      "address",
      "lat",
      "lon",
    ];

    for (const field of requiredFields) {
      if (!formData[field as keyof BusinessInfo]) {
        AlertService.warning(
          `Please fill out the ${field.replace("_", " ")} field.`,
          "Missing Information"
        );
        return false;
      }
    }
    return true;
  };

  // ---- Submit (Create or Update) ----
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const confirm = await AlertService.confirm(
      isEditMode
        ? "Update these business details?"
        : "Create new business entry?"
    );
    if (!confirm) return;

    setSaving(true);

    let mapImage = formData.map_image;
    
    const form = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        form.append(key, value as any);
      }
    });

    if (!mapImage && mapRef.current) {
      mapImage = await mapRef.current.generateStaticImage();
      if (mapImage) {
        form.append("map_image", mapImage);
      }
    }

    try {
      if (isEditMode && formData.id) {
        // ---- Update existing business ----
        await _post(apiUrls.updateBusiness, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire("Success!", "Business info updated successfully!", "success");
      } else {
        // ---- Create new business ----
        await _post(apiUrls.updateBusiness, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire("Success!", "New business created successfully!", "success");
      }

      navigate("/business-info");
    } catch (error: any) {
      if (error.response?.data?.errors) {
        const messages = Object.values(error.response.data.errors)
          .flat()
          .join("\n");
        Swal.fire("Validation Error", messages, "error");
      } else {
        Swal.fire("Error", "Failed to save business info", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        {isEditMode ? "Edit Business Info" : "Create New Business"}
        <Button
          className="float-end"
          variant="dark"
          onClick={() => navigate("/business-info")}
        >
          Back
        </Button>
      </h1>

      <Card className="d-shadow">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-8 mb-3">
                <Row>
                  <Col md={6}>
                    <TextInput
                      label="Business Name"
                      name="name"
                      value={formData.name}
                      required
                      onChange={handleFieldChange}
                    />
                  </Col>
                  <Col md={6}>
                    <TextInput
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      required
                      onChange={handleFieldChange}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <TextInput
                      label="Mobile Number"
                      name="mobile_no"
                      value={formData.mobile_no}
                      required
                      onChange={handleFieldChange}
                    />
                  </Col>
                  <Col md={6}>
                    <FileInput
                      label="Business Logo"
                      name="logo"
                      existing={preview}
                      multiple={false}
                      onChange={handleLogoChange}
                    />
                  </Col>
                </Row>

                <LocationSelector
                  setClass="col-md-6"
                  division_id={formData.division_id}
                  district_id={formData.district_id}
                  thana_id={formData.thana_id}
                  postal_code={formData.postal_code}
                  onChange={handleFieldChange}
                  required
                />

                <TextInput
                  label="Address"
                  name="address"
                  value={formData.address}
                  required
                  onChange={handleFieldChange}
                />
              </div>
              <div className="col-md-4 mb-3">
                <Form.Group className="mb-3">
                  <Form.Label>Business Location</Form.Label>
                  <LocationPickerMap
                    ref={mapRef}
                    mode="create"
                    value={formData.lat && formData.lon ? { lat: formData.lat, lng: formData.lon } : null}
                    onChange={(loc) =>
                      setFormData((p) => ({ ...p, lat: loc?.lat, lon: loc?.lng }))
                    }
                  />
                  {/* <div className="mt-2 text-muted">
                    <label className="d-inline fw-400">Lat:</label>{" "}
                    {formData.lat} | Lon: {formData.lon}
                  </div> */}
                </Form.Group>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
                className="px-4"
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" />{" "}
                    {isEditMode ? "Updating..." : "Saving..."}
                  </>
                ) : isEditMode ? (
                  "Update Business Info"
                ) : (
                  "Save Business Info"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}
